import { useEffect, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, PencilLine } from 'lucide-react';
import { CUSTOM_LINE_DEFAULT_UNIT } from '../lib/priceListCustomLines';

export interface ManualLineValues {
  code: string;
  name: string;
  description: string;
  unit: string;
  category: string;
  price: number;
  quantity: number;
  discountPercentage: number;
}

interface ManualLineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: ManualLineValues) => Promise<void> | void;
  initialValues?: Partial<ManualLineValues> | null;
  isSaving?: boolean;
}

const EMPTY_FORM = {
  code: '',
  name: '',
  description: '',
  unit: CUSTOM_LINE_DEFAULT_UNIT,
  category: '',
  price: '0',
  quantity: '1',
  discountPercentage: '0',
};

type FormState = typeof EMPTY_FORM;

const parseDecimal = (raw: string): number => {
  const parsed = parseFloat((raw || '').replace(',', '.'));
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
};

const parseQuantity = (raw: string): number => {
  const parsed = parseFloat((raw || '').replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

export function ManualLineModal({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  isSaving = false,
}: ManualLineModalProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!initialValues;

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setForm({
      code: initialValues?.code ?? '',
      name: initialValues?.name ?? '',
      description: initialValues?.description ?? '',
      unit: initialValues?.unit || CUSTOM_LINE_DEFAULT_UNIT,
      category: initialValues?.category ?? '',
      price: initialValues?.price !== undefined ? String(initialValues.price) : '0',
      quantity: initialValues?.quantity !== undefined ? String(initialValues.quantity) : '1',
      discountPercentage:
        initialValues?.discountPercentage !== undefined ? String(initialValues.discountPercentage) : '0',
    });
  }, [isOpen, initialValues]);

  const updateField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfirm = async () => {
    const name = form.name.trim();
    if (!name) {
      setError('La descrizione è obbligatoria.');
      return;
    }

    setError(null);
    await onSubmit({
      code: form.code.trim(),
      name,
      description: form.description.trim(),
      unit: form.unit.trim() || CUSTOM_LINE_DEFAULT_UNIT,
      category: form.category.trim(),
      price: parseDecimal(form.price),
      quantity: parseQuantity(form.quantity),
      discountPercentage: parseDecimal(form.discountPercentage),
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="z-[90] flex flex-col gap-3 overflow-hidden sm:max-w-lg" overlayClassName="z-[90]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PencilLine className="w-5 h-5 text-primary-600" />
            {isEditing ? 'Modifica riga manuale' : 'Nuova riga manuale'}
          </DialogTitle>
          <DialogDescription>
            Voce libera non collegata all'anagrafica prodotti. Potrai creare il prodotto in seguito.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3 px-1">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">Codice</Label>
              <Input
                value={form.code}
                onChange={(e) => updateField('code', e.target.value)}
                placeholder="es. SERV-01"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Descrizione *</Label>
              <Input
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="es. Installazione in sede"
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Dettaglio (opzionale)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Testo aggiuntivo stampato sotto la descrizione"
              rows={2}
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Unità di misura</Label>
              <Input
                value={form.unit}
                onChange={(e) => updateField('unit', e.target.value)}
                placeholder={CUSTOM_LINE_DEFAULT_UNIT}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Categoria (opzionale)</Label>
              <Input
                value={form.category}
                onChange={(e) => updateField('category', e.target.value)}
                placeholder="es. Servizi"
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Prezzo (€)</Label>
              <Input
                inputMode="decimal"
                value={form.price}
                onChange={(e) => updateField('price', e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Quantità</Label>
              <Input
                inputMode="decimal"
                value={form.quantity}
                onChange={(e) => updateField('quantity', e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sconto (%)</Label>
              <Input
                inputMode="decimal"
                value={form.discountPercentage}
                onChange={(e) => updateField('discountPercentage', e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Annulla
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isSaving} className="bg-primary-600 hover:bg-primary-700">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEditing ? 'Salva' : 'Aggiungi riga'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ManualLineModal;
